import BaseAPI from './api';
import env from '../environment';
import { AppConstants } from '../constants/AppConstants';
import { isEmpty } from 'lodash';

const globalHeaders = {
  'Content-Type': 'application/json'
};

const API = BaseAPI?.axiosInstance;

export const listRoles = async (
  name: string,
  ignorePermissions: boolean = false,
  page: number = AppConstants.DEFAULT_PAGE,
  size: number = AppConstants.DEFAULT_PAGE_SIZE,
  sort: string = AppConstants.DEFAULT_SORT_BY,
  sortType: string = AppConstants.DEFAULT_SORT_DESC,
  filters: any = {}
) => {
  const headers = {
    ...globalHeaders,
    apikey: env.apikey
  };
  let uri = `${env.accessControlPath}/roles?page=${page}&size=${size}`;
  if (name) {
    uri += `&name=${name.trim()}`;
  }
  if (ignorePermissions) {
    uri += `&${AppConstants.QUERY_PARAM_IGNORE_PERMISSIONS}=${ignorePermissions}`;
  }
  if (sort) {
    uri += `&sort=${sort},${sortType}`;
  }

  if (!isEmpty(filters)) {
    if (filters['orgFilter'] && filters['orgFilter'].length) {
      const [orgData] = filters['orgFilter'];
      uri += `&organizationId=${orgData.id}`;
    }
  }
  const response = await API.get(uri, { headers }).then((res) => res);
  return response;
};

export const createRole = async (body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.post(`${env.accessControlPath}/roles`, body, { headers });
  return response;
};

export const updateRole = async (id: string, body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.put(`${env.accessControlPath}/roles/${id}`, body, {
    headers
  });
  return response;
};

export const getRole = async (roleId: any, includeFeatures: boolean = false) => {
  const headers = { ...globalHeaders };
  let url = `${env.accessControlPath}/roles/${roleId}`;
  if (includeFeatures) {
    url += '?includeFeatures=true';
  }
  return await API.get(url, { headers });
};

export const deleteRole = async (roleId: any) => {
  const headers = { ...globalHeaders };
  return await API.delete(`${env.accessControlPath}/roles/${roleId}`, { headers });
};

export const getPermissions = async (size: number = 5000) => {
  const headers = { ...globalHeaders };
  return await API.get(`${env.accessControlPath}/permissions?size=${size}`, { headers });
};

export const listFieldGropus = async (
  size: number,
  page: number,
  name: string = '',
  sorters: string = ''
) => {
  const headers = { ...globalHeaders };
  let uri = `${env.accessControlPath}/field-groups?size=${size}&page=${page}`;
  if (name) {
    uri += `&name=${name.trim()}`;
  }
  if (sorters) {
    uri += `&sort=${sorters}`;
  }
  const response = await API.get(uri, { headers });
  return response;
};

export const listConsentTemplates = async (
  size: number,
  page: number,
  name: string = '',
  filters: string = '',
  sorters: string = ''
) => {
  const headers = { ...globalHeaders };
  let uri = `${env.accessControlPath}/consent/templates?size=${size}&page=${page}`;

  if (name) {
    uri += `&name=${name.trim()}`;
  }

  if (filters) {
    uri += `&${filters}`;
  }

  if (sorters) {
    uri += `&sort=${sorters}`;
  }
  const response = await API.get(uri, { headers });
  return response;
};

export const getConsentAssignByGiverId = async (
  size: number,
  page: number,
  giverId: string = ''
) => {
  const headers = { ...globalHeaders };
  let uri = `${env.accessControlPath}/consent/assignments/givers/${giverId}?size=${size}&page=${page}`;

  const response = await API.get(uri, { headers });
  return response;
};

export const createConsentTemplate = async (body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.post(`${env.accessControlPath}/consent/templates`, body, { headers });
  return response;
};

export const createConsentAssignment = async (body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.post(`${env.accessControlPath}/consent/assignments`, body, {
    headers
  });
  return response;
};

export const updateConsentTemplate = async (id: string, body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.put(`${env.accessControlPath}/consent/templates/${id}`, body, {
    headers
  });
  return response;
};

export const deleteConsentTemplate = async (id: string) => {
  const headers = { ...globalHeaders };
  const response = await API.delete(`${env.accessControlPath}/consent/templates/${id}`, {
    headers
  });
  return response;
};

export const createNewVersiononsentTemplate = async (id: string) => {
  const headers = { ...globalHeaders };
  const response = await API.post(
    `${env.accessControlPath}/consent/templates/${id}/version`,
    {},
    {
      headers
    }
  );
  return response;
};

export const addPermissionsToRole = async (roleId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/roles/${roleId}/permissions`, body, {
    headers
  });
};

export const deletePermissionsFromRole = async (roleId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.delete(`${env.accessControlPath}/roles/${roleId}/permissions`, {
    data: body,
    headers
  });
};

export const addAttachmentsToConsent = async (id: string, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/consent/templates/${id}/attachments`, body, {
    headers
  });
};

export const removeAttachmentsFromConsent = async (id: string, attachmentId: string) => {
  const headers = { ...globalHeaders };
  return await API.delete(
    `${env.accessControlPath}/consent/templates/${id}/attachments/${attachmentId}`,
    {
      headers
    }
  );
};

export const updateAttachmentsOfConsent = async (id: string, attachmentId: string, body: any) => {
  const headers = { ...globalHeaders };
  return await API.put(
    `${env.accessControlPath}/consent/templates/${id}/attachments/${attachmentId}`,
    body,
    {
      headers
    }
  );
};

export const listFeatures = async (
  name: string,
  page: number = AppConstants.DEFAULT_PAGE,
  size: number = AppConstants.DEFAULT_PAGE_SIZE,
  sort: string = AppConstants.DEFAULT_SORT_BY,
  sortType: string = AppConstants.DEFAULT_SORT_DESC,
  filters: any = {}
) => {
  const headers = {
    ...globalHeaders,
    apikey: env.apikey
  };
  let uri = `${env.accessControlPath}/features?page=${page}&size=${size}`;
  if (name) {
    uri += `&name=${name.trim()}`;
  }
  if (sort) {
    uri += `&sort=${sort},${sortType}`;
  }

  if (!isEmpty(filters)) {
    if (filters['status'] && filters['status'].length) {
      const [val] = filters['status'];
      uri += `&status=${val.key}`;
    }

    if (filters['actions'] && filters['actions'].length) {
      uri += `&actions=${filters.actions.split(',')}`;
    }

    if (filters['tags'] && filters['tags'].length) {
      uri += `&tags=${filters.tags.split(',')}`;
    }


    if (filters['orgFilter'] && filters['orgFilter'].length) {
      const [val] = filters['orgFilter'];
      uri += `&organization-id=${val.id}`;
    }
  }
  const response = await API.get(uri, { headers }).then((res) => res);
  return response;
};

export const createFeature = async (body: any) => {
  const headers = { ...globalHeaders };
  const response = await API.post(`${env.accessControlPath}/features`, body, { headers });
  return response;
};

export const getFeature = async (featureId: any) => {
  const headers = { ...globalHeaders };
  return await API.get(`${env.accessControlPath}/features/${featureId}`, { headers });
};

export const updateFeature = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.put(`${env.accessControlPath}/features/${featureId}`, body, { headers });
};

export const deleteFeature = async (featureId: any) => {
  const headers = { ...globalHeaders };
  return await API.delete(`${env.accessControlPath}/features/${featureId}`, { headers });
};

export const updateFeatureOperations = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.patch(`${env.accessControlPath}/features/operations/${featureId}`, body, {
    headers
  });
};

export const updateOperationAPIPermissions = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(
    `${env.accessControlPath}/features/operations/${featureId}/permissions`,
    body,
    {
      headers
    }
  );
};

export const addOperationOrganizations = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(
    `${env.accessControlPath}/features/${featureId}/organizations`,
    body,
    {
      headers
    }
  );
};

export const removeOperationOrganizations = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(
    `${env.accessControlPath}/features/${featureId}/organizations/remove`,
    body,
    {
      headers
    }
  );
};


export const deleteFeatureOperations = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/features/${featureId}/operations/remove`, body, {
    headers
  });
};

export const addOperationsToFeature = async (featureId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/features/${featureId}/operations`, body, {
    headers
  });
};

export const assignFeatureOperationsRole = async (roleId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/roles/${roleId}/features`, body, {
    headers
  });
};

export const deleteFeatureOperationsRole = async (roleId: any, body: any) => {
  const headers = { ...globalHeaders };
  return await API.post(`${env.accessControlPath}/roles/${roleId}/features/remove`, body, {
    headers
  });
};
